import { useMemo } from 'react';

import { useContacts } from '../hooks';
import { useFetch } from '../../hooks';
import { DateFilter } from '../../components';
import { useAppContext } from '../../molecules';
import { getDistinctContactStates } from '../../api';
import { Form, FormItem, TopPanel } from '../../components';
import { SearchBar, Dropdown, Table } from '../../components';
import type { ContactItemType } from '../hooks';
import type { ColumnType, ButtonProps } from '../../components';

const toLabel = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

type FiltersType = NonNullable<ReturnType<typeof useContacts>['filters']>;

type ContactsListTemplateProps = {
  buttons?: ButtonProps[];
  columns: ColumnType<ContactItemType>[];
  listResult: ReturnType<typeof useContacts>;
  filtersClassNames?: { [x in keyof FiltersType]?: string };

  children?: React.ReactNode;
};

const ContactsListTemplate: React.FC<ContactsListTemplateProps> = ({
  buttons,
  columns,
  children,
  listResult,
  filtersClassNames = {},
}) => {
  const { meta } = useAppContext();
  const { nodes, initializing, filters, setFilters, sort, setSort } =
    listResult;

  const campaignId = filters?.campaignId;
  const { data: statesFromApi } = useFetch(
    () => getDistinctContactStates(campaignId),
    [campaignId]
  );

  const stateOptions = useMemo(() => {
    const opts: { value: LatestContactState; label: string }[] = [
      { value: undefined, label: 'All Statuses' },
    ];
    if (statesFromApi) {
      statesFromApi.forEach(s => opts.push({ value: s, label: toLabel(s) }));
    }
    opts.push({ value: 'null', label: 'No State' });
    return opts;
  }, [statesFromApi]);

  return (
    <div className="flex flex-col gap-y-md w-full">
      <Form<NonNullable<typeof filters>>
        initialValue={filters}
        onChange={setFilters}
      >
        <TopPanel
          buttons={buttons}
          topLeftContent={
            <FormItem<NonNullable<typeof filters>> name="search" label="">
              <SearchBar />
            </FormItem>
          }
        >
          <>
            <FormItem<NonNullable<typeof filters>>
              label=""
              name="latest_contact_state"
            >
              <Dropdown<LatestContactState>
                grow={false}
                options={stateOptions}
                placeholder="All Statuses"
                className={filtersClassNames['latest_contact_state']}
              />
            </FormItem>
            <FormItem<NonNullable<typeof filters>>
              label=""
              name="requiresHumanInput"
            >
              <Dropdown
                grow={false}
                placeholder="Human Input"
                className={filtersClassNames['requiresHumanInput']}
                options={[
                  { value: '', label: 'All Contacts' },
                  { value: 'true', label: 'Requires Human Input' },
                  { value: 'false', label: 'No Human Input Required' },
                ]}
              />
            </FormItem>
            <FormItem<NonNullable<typeof filters>> name="campaignId" label="">
              <Dropdown
                grow={false}
                placeholder="Campaign"
                className={filtersClassNames['campaignId']}
                options={[
                  { value: '', label: 'All Campaigns' },
                  ...meta.campaigns,
                ]}
              />
            </FormItem>
            <DateFilter<NonNullable<typeof filters>>
              toName="updatedTo"
              fromName="updatedFrom"
            />
          </>
        </TopPanel>
      </Form>
      {children}
      <div className="flex-1 overflow-y-auto">
        <Table
          data={nodes}
          loading={initializing}
          sort={sort}
          onSort={setSort}
          config={{ columns: columns }}
        />
      </div>
    </div>
  );
};

export default ContactsListTemplate;
